'use client';

import ease from '@/constants/ease';
import useStore from '@/stores/useStore';
import "@/styles/libs/arrow-animation.css";
import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, useEffect, useState } from 'react';

const TESTIMONIAL_INTERVAL_MS = 5000;

interface CustomerStoryTestimonialProps {
  logo: React.ReactNode;
  quote: string;
}

const TestimonialProgressBubbles = ({
  testimonials,
  currentTestimonialIndex,
  SetFixedTestimonial,
  isAnimating,
}: {
  testimonials: CustomerStoryTestimonialProps[];
  currentTestimonialIndex: number;
  SetFixedTestimonial: (index: number) => void;
  isAnimating: boolean;
}) => {
  return (
    <>
      {testimonials.map((_, index) => {
        const duration = isAnimating ? TESTIMONIAL_INTERVAL_MS / 1000 : 1;
        // isAnimating is the auto play state - animation is slower per TESTIMONIAL_INTERVAL_MS

        return isAnimating ? (
          <motion.div
            key={`progress-bubble-${index}`}
            className="bg-violet-600 relative overflow-hidden cursor-pointer mr-[4px]"
            initial={{ width: '12px', height: '1px' }}
            animate={{ width: index === currentTestimonialIndex ? ['12px', '24px', '24px', '12px'] : '12px' }}
            transition={{ duration: duration, times: [0, 0.1, 0.9, 1] }}
            onClick={() => SetFixedTestimonial(index)}
          >
            <motion.div
              className="w-[0px] h-[1px] absolute z-1  bg-violet-300 "
              initial={{ width: '0', left: '0', right: '0' }}
              animate={{
                width: index === currentTestimonialIndex ? ['0px', '24px', '0px'] : '0px',
              }}
              transition={{ duration: duration, times: [0, 0.9, 1] }}
              onClick={() => SetFixedTestimonial(index)}
            />
          </motion.div>
        ) : (
          // Not animating state - triggered by clicking on a bubble - selected bubble fills in 1s and then stays there
          <motion.div
            key={`progress-bubble-${index}`}
            className="bg-violet-600 relative overflow-hidden cursor-pointer mr-[4px]"
            initial={{ width: '12px', height: '1px' }}
            animate={{ width: index === currentTestimonialIndex ? ['12px', '24px'] : '12px' }}
            transition={{ duration: duration, times: [0, 0.9] }}
            onClick={() => SetFixedTestimonial(index)}
          >
            <motion.div
              initial={{ width: '0px', left: '0', right: '0' }}
              animate={{
                width: index === currentTestimonialIndex ? ['0px', '24px'] : '0px',
              }}
              transition={{ duration: duration, times: [0, 0.9] }}
              className="w-[12px] h-[1px] absolute z-1 bg-violet-300 "
              onClick={() => SetFixedTestimonial(index)}
            />
          </motion.div>
        );
      })}
    </>
  );
};

const DesktopCustomerStoryTestimonial = ({ testimonials }: { testimonials: CustomerStoryTestimonialProps[] }) => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  const SetFixedTestimonial = (index: number) => {
    setIsAnimating(false);
    setCurrentTestimonialIndex(index);
  };

  useEffect(() => {
    if (isAnimating) {
      const interval = setInterval(() => {
        setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
      }, TESTIMONIAL_INTERVAL_MS);

      return () => clearInterval(interval);
    }
  }, [testimonials, currentTestimonialIndex, isAnimating]);

  return (
    <div className="relative h-full w-full">
      <AnimatePresence>
        {testimonials.map((testimonial, index) => {
          return (
            <motion.div
              key={`customer-story-testimonial-${index}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 w-full h-full  flex justify-between"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="flex"
              >
                <div className="flex items-center">
                  <div className="relative flex text-blue-100 font-500 text-h4">
                    {testimonial.quote}

                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })}
        {/* Progress bubbles */}
        <div className="absolute z-10 left-4 bottom-0 -translate-x-1/2 flex">
          <TestimonialProgressBubbles
            testimonials={testimonials}
            currentTestimonialIndex={currentTestimonialIndex}
            SetFixedTestimonial={SetFixedTestimonial}
            isAnimating={isAnimating}
          />
        </div>
      </AnimatePresence>
    </div>
  );
};

export interface AiFeatureSetProps {
  aiFeatures: {
    icon: React.ReactNode;
    title: string;
    description: string;
    price: string;
    priceDescription: string;
  }[],
  testimonials: CustomerStoryTestimonialProps[];
}

const AiFeatureSet: FC<AiFeatureSetProps> = ({
  aiFeatures = [], testimonials = [],
}) => {
  const { setDemoModalOpen, setTrialModalOpen } = useStore();

  const onClickMenuButton = (action) => {
    switch (action) {
      case 'trial':
        setTrialModalOpen(true);
        break;
      case 'demo':
        setDemoModalOpen(true);
        break;

      default:
        break;
    }
  };

  return (
    <>
      <section className="flex pt-[80px] md:pt-[120px] bg-violet-900 px-[24px] bg-no-repeat bg-[url('https://front.com/webassets/rebrand/ai-blur.svg')]" style={{ backgroundPosition: "center calc(100% - 60px)" }}>
        <div className="w-full mx-auto max-w-grid flex flex-wrap justify-center md:grid grid-cols-4 gap-x-2 rounded-12">
          <div className="col-span-full text-violet-200 text-micro3 text-center uppercase">
            Front AI Add-ons
          </div>
          <div className="col-span-full text-center text-blue-100 font-500 text-h1 my-[12px]">
            Power up your plan with Front AI
          </div>
          <div className="col-span-full text-center text-violet-200 text-p2 mb-[80px]">
            Meet Front AI: human-in-control AI that powers<br /> exceptional service on even your toughest tickets.
          </div>
          {aiFeatures.map((feature, index) => (
            <div key={`ai-feature-${index}`} className="flex flex-wrap bg-violet-800 rounded-[8px] p-[32px] items-start w-full mb-[24px] md:mb-0">
              <div className="flex w-full flex-wrap items-start">
                <div className="flex w-[48px] h-[48px] bg-citron-500 justify-center items-center rounded-[8px]">
                  {feature.icon}
                </div>
                <div className="flex w-full text-blue-100 font-500 text-h3 mt-[20px] mb-[8px]">
                  {feature.title}
                </div>
                <div className="flex w-full text-violet-200 text-p2">
                  {feature.description}
                </div>
              </div>
              <div className="flex w-full flex-wrap items-end mt-[32px] md:mt-[48px]">
                <div className="flex w-full text-violet-200 text-caption2">
                  <span>
                    <span className="text-blue-100 font-500 text-p2">{feature.price}</span> {feature.priceDescription}
                  </span>
                </div>
                <button
                  className="flex font-500 text-white text-p2 items-center mt-[18px] hover-arrow-wrapper"
                  onClick={() => {
                    onClickMenuButton("demo");
                  }}
                >
                  <span>Talk to Sales</span>
                  <div className="flex ml-[8px] relative w-[16px] h-[16px] bg-[url(https://front.com/webassets/rebrand/arrow-right.png)] hover-arrow" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="flex pt-0 md:pt-[24px] bg-violet-900 pb-[80px] px-[24px]">
        <div className="w-full mx-auto max-w-grid flex flex-wrap md:grid grid-cols-2 gap-x-2 rounded-12">
          <div className="hidden md:flex relative rounded-[12px] p-[32px] border-solid border-[1px] border-violet-800">
            <DesktopCustomerStoryTestimonial testimonials={testimonials} />
          </div>
          <div className="flex w-full rounded-[12px] p-[32px] bg-violet-800">
            <div className="group flex flex-wrap md:flex-nowrap items-center">
              <div className="flex relative w-[140px] mt-[18px] mb-[54px] md:mb-0 md:mt-0">
                <div className="flex w-[56px] h-[56px] bg-citron-500 rounded-[8px] items-center justify-center px-[10px] absolute top-[-24px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 20 20" fill="none">
                    <g clip-path="url(#clip0_1267_12743)">
                      <path d="M10.0001 18.3327C14.6025 18.3327 18.3335 14.6017 18.3335 9.99935C18.3335 5.39698 14.6025 1.66602 10.0001 1.66602C5.39777 1.66602 1.66681 5.39698 1.66681 9.99935C1.66681 14.6017 5.39777 18.3327 10.0001 18.3327Z" stroke="#001B38" strokeWidth="1.62886" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10.0001 15C12.7615 15 15.0001 12.7614 15.0001 10C15.0001 7.23858 12.7615 5 10.0001 5C7.23864 5 5.00006 7.23858 5.00006 10C5.00006 12.7614 7.23864 15 10.0001 15Z" stroke="#001B38" strokeWidth="1.62886" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10.0001 11.6654C10.9206 11.6654 11.6668 10.9192 11.6668 9.9987C11.6668 9.07822 10.9206 8.33203 10.0001 8.33203C9.07963 8.33203 8.33344 9.07822 8.33344 9.9987C8.33344 10.9192 9.07963 11.6654 10.0001 11.6654Z" stroke="#001B38" strokeWidth="1.62886" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip0_1267_12743">
                        <rect width="20" height="20" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>

                <div className="flex w-[56px] h-[56px] bg-citron-500 rounded-[8px] items-center justify-center px-[10px] absolute top-[-24px] border-violet-800 border-solid border-l-[3px] left-[40px]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 20 20" fill="none">
                    <path d="M10 16.6673V8.33398" stroke="#001B38" strokeWidth="1.79199" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M15 16.6673V3.33398" stroke="#001B38" strokeWidth="1.79199" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 16.6673V13.334" stroke="#001B38" strokeWidth="1.79199" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              <div className="relative flex flex-wrap ml-0 md:ml-[24px]">
                <div className="flex text-blue-100 font-500 text-h4">
                  Smart QA + Smart CSAT bundle
                </div>
                <div className="flex text-violet-200 text-p2">
                  Complete performance insights for $25 / seat / mo
                </div>
                <motion.div
                  className={`absolute hidden group-hover:block right-0 bg-violet-800 h-[80px] pl-[40px] w-[325px]`}
                  style={{ background: "linear-gradient(270deg, #5B1F76 58.39%, rgba(91, 31, 118, 0.00) 100%)" }}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 400 / 1000,
                    ease: ease.appear,
                  }}
                >
                  <button
                    className="absolute right-0 flex font-500 text-white text-p2 items-center mt-[18px] hover-arrow-wrapper"
                    onClick={() => {
                      onClickMenuButton("demo");
                    }}
                  >
                    <span>Talk to Sales</span>
                    <div className="flex ml-[8px] relative w-[16px] h-[16px] bg-[url(https://front.com/webassets/rebrand/arrow-right.png)] hover-arrow" />
                  </button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AiFeatureSet;
